import React, {
  CSSProperties,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { snapOrFromTo } from 'animations/snapTween';
import { audioPath, OPPONENT_ACTION_VOLUME } from 'constants/audioConstants';
import {
  SELF_BET_ABOVE_SEAT_PX,
  SELF_BET_BOTTOM_BAND_PCT,
  TRAY_OPEN_BET_PILL_LIFT_FALLBACK_PX,
  TRAY_OPEN_CARD_LIFT_MS,
  TRAY_OPEN_PILL_CARD_GAP_PX,
  TRAY_OPEN_PILL_INITIAL_TRAVEL_FRACTION
} from 'constants/gameConstants';
import GameContext from 'contexts/GameContext';
import PovCardsContext from 'contexts/PovCardsContext';
import TableSettingsContext from 'contexts/TableSettingsContext';
import gsap from 'gsap';
import { SeatPosition } from 'hooks/useEllipticalPositions';
import { useOrientation } from 'hooks/useOrientation';
import useSound from 'hooks/useSound';
import { useTableSelector } from 'hooks/useTableSelector';
import { useTableArrivalSnap, useTableSnap } from 'hooks/useTableSnap';
import { TRAY_CLEARANCE_GAP_PX, visualScaleOf } from 'hooks/useTrayClearanceLift';
import { tableMoneyFormatterCompact } from 'utils/functions';
import { resolveChipCue, shouldPlayTableSfx } from 'utils/tableSfx';

import { povSeatTop } from 'components/Seat/modes/getPortraitModeStyles';

import BetChips from './BetChips';
import { CHIPS_DELAY } from './Chips';
import { DEALER_DELAY } from './DealerButton';
import PokerChip from './PokerChip';
import ReturnedBetChips from './ReturnedBetChips';
import ReturnedBetMessage from './ReturnedBetMessage';

type BetPropType = {
  position: SeatPosition;
  seatPosition?: SeatPosition;
  seat: any;
  seatId: number;
};

/**
 * Where a bet pill sits vertically, as a CSS length.
 *
 * Only the CURRENT player's pill is moved, and only in portrait's bottom band:
 * the ring puts the POV seat's bet on top of the player's own hole cards.
 * Everyone else's pill keeps its raw ring position — an opponent's cards are
 * tiny and nowhere near their bet.
 *
 * The POV pill is anchored to its own SEAT rather than to a percentage of the
 * table, so the air between it and the dealer marker hanging off that seat is a
 * fixed number of scaled px instead of a sum of container-%, viewport-vh and px
 * that only balanced on one device. See SELF_BET_ABOVE_SEAT_PX.
 *
 * Exported for unit testing.
 */
export const selfBetTop = (
  betTopPct: number,
  seatTopPct: number | undefined,
  isSelfSeat: boolean,
  isPortrait: boolean
): string => {
  const raw = `${betTopPct}%`;
  // Landscape lays the POV seat out as a block and is deliberately untouched;
  // the band check keeps a future ring that moves the POV off the bottom of the
  // felt from silently inheriting a rule written for a bottom seat.
  if (!isSelfSeat || !isPortrait || betTopPct <= SELF_BET_BOTTOM_BAND_PCT) return raw;
  if (seatTopPct === undefined) return raw;
  // CSS `min` because the seat anchor and the ring position are in units that
  // cannot be compared in JS. It keeps the rule a CAP, exactly as the old
  // Math.min did: it may pull the pill UP off the player's cards, never push it
  // down onto them. On a ring that already places the bet high — portrait on a
  // desktop-shaped window — the ring keeps winning and nothing moves.
  return `min(${raw}, calc(${povSeatTop(
    seatTopPct
  )} - ${SELF_BET_ABOVE_SEAT_PX}px * var(--seat-scale, 1)))`;
};

const Bet = ({ position, seatPosition, seat, seatId }: BetPropType) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  // True while this table is snapping to the state the player arrived at (table
  // switch / reconnect resync). A bet that was already on the felt then must
  // simply BE there — no pop-in, no chip sound, no staged reveal.
  const shouldSnap = useTableSnap();
  // The ARRIVAL half of the snap on its own — depends only on the gameID, so
  // every mounted copy of this table (carousel panel AND grid cell) agrees.
  // The blinds-reveal stagger below rides this, not `shouldSnap`, precisely so
  // the copies stage `isShow` identically (dev review #3747 round 3).
  const arrivalSnap = useTableArrivalSnap();
  // Tracks the pop-in tween so it can be killed on re-run/unmount — otherwise a
  // bet pill that unmounts mid-pop orphans the tween against a detached node.
  const popTweenRef = useRef<gsap.core.Tween | null>(null);
  const hand = useTableSelector((table) => table.game.hand);
  const tableOverBet = useTableSelector((table) => table.game.tableOverBet);
  const [isShow, setIsShow] = useState<boolean>(false);
  // OWN vs OPPONENT, for both chip samples (EN-10585 AC1/AC2, dev review
  // #3747; the shape mirrors pages/Game.tsx's action cues). This component
  // cues every REAL wager — bet, call, raise, blind; the ante is the one
  // wager cued from Game.tsx — so the split has to hold here too.
  //
  // Your own wager is how your turn ended (you called, you bet, your blind
  // posted), so it must reach you from a table you are not looking at:
  // crossTable, plus a per-play dedupeKey at the call site because every
  // mounted copy of this table (carousel panel AND grid cell) fires it.
  // An opponent's wager is ambient table texture: table-local, the same
  // sample so the action→sound pairing stays learnable, but attenuated.
  const [playBetChipsOwn] = useSound(audioPath.betChips, { crossTable: true });
  const [playBetChips] = useSound(audioPath.betChips, { volume: OPPONENT_ACTION_VOLUME });
  // The single-chip sample, for a wager at or under the big blind — see
  // resolveChipCue. All four are held so the choice is made at fire time.
  const [playSmallBetOwn] = useSound(audioPath.smallBet, { crossTable: true });
  const [playSmallBet] = useSound(audioPath.smallBet, { volume: OPPONENT_ACTION_VOLUME });
  const [playerBet, setPlayerBet] = useState(0);
  const [playChipsToPot] = useSound(audioPath.chipsToPot);
  const [newPlayerBet, setNewPlayerBet] = useState(playerBet);

  // Tracks the previous server-reported betDisplay so we can detect the
  // real positive → 0 transition (chips actually collected to pot) and
  // avoid firing the chips-to-pot side effects on mount/remount/resize.
  const prevBetValueRef = useRef<number | null>(null);

  // The own-wager cue's ARMED EDGE: set to the wagered amount on the betDisplay
  // wire transition, consumed (and nulled) when the pill's reveal fires the cue
  // in the isShow effect below. This is what makes the mount guard structural:
  // a copy that mounts with the bet already on the felt never arms an edge, so
  // its reveal plays nothing — regardless of useSound's arrival window
  // (dev review #3747 round 3).
  const pendingOwnCueRef = useRef<number | null>(null);

  // The table's big blind — the threshold that decides which chip sample a
  // wager gets (resolveChipCue). Tracked in a ref because it is read at cue
  // time, not render time: putting it in the cue effect's dependencies would
  // let a tournament blind level rising mid-hand replay a chip sound.
  const bigBlind = useTableSelector((table) => table.game.bb);
  const bigBlindRef = useRef(bigBlind);
  bigBlindRef.current = bigBlind;

  const tableType = useTableSelector((table) => table.game.tabletype);
  const currentPlayerSeatId = useTableSelector((table) => table.currentPlayer.seatId);
  const { muteOtherPlayers, muteOtherPlayersSetByUser } = useContext(TableSettingsContext);
  // Also drives the bet-pill position cap in portrait (see the `top` style below).
  const isSelfSeat = seatId === currentPlayerSeatId;
  // Self-triggered sounds always play; others' sounds get squelched when the
  // player has muted opponents in Table Settings (so they only hear their own
  // table noises and know when it's their turn). Same rule as every other table
  // SFX — see utils/tableSfx.
  const shouldPlayBetSounds = shouldPlayTableSfx({
    origin: 'player',
    actorSeatId: seatId,
    currentPlayerSeatId,
    muteOtherPlayersSounds: muteOtherPlayers,
    muteOtherPlayersIsExplicit: muteOtherPlayersSetByUser
  });

  // EN-10420: while the bet/raise tray is open, the POV's decision group lifts
  // out from under it (Seat/modes/PortraitCurrentPlayer) — and this chip, which
  // sits just above those cards, rides up with them so it never lands ON the
  // lifted cards.
  //
  // PORTRAIT ONLY. In landscape the whole seat rises as a block instead, and the
  // chip should rise with it by the identical amount — not yet wired: the copy
  // resolved to 0 travel and the chip can end up crowding the lifted timer ring
  // on short landscape canvases. Cosmetic, landscape-only, tracked separately.
  const { isBetTrayOpen } = useContext(GameContext);
  const isPortrait = useOrientation() === 'portrait';
  const liftForTray = isBetTrayOpen && isSelfSeat && isPortrait;

  // The pill's travel is MEASURED, not hardcoded: pill (percentage-anchored)
  // and cards (seat-anchored) live in different coordinate systems, so a fixed
  // offset drifted across device sizes and after rotation. Target: pill bottom
  // = lifted cards top − TRAY_OPEN_PILL_CARD_GAP_PX. Both rects are normalized
  // back to their untransformed positions (computed-matrix ty for the cards,
  // our own previous lift for the pill), so it does not matter whether the
  // 220ms transitions are mid-flight or a rotation remount applied transforms
  // instantly.
  const { refs: povCardsRefs } = useContext(PovCardsContext);
  const [trayLiftPx, setTrayLiftPx] = useState(0);
  const trayLiftPxRef = useRef(0);
  useLayoutEffect(() => {
    const apply = (px: number) => {
      trayLiftPxRef.current = px;
      setTrayLiftPx(px);
    };
    // Not lifting (tray closed / not our seat / landscape): park at 0 and stop.
    // The measurement work and listeners below exist ONLY while lifted — this
    // guard is also what glides the pill back down when the tray closes. If the
    // component unmounts while lifted, React runs the cleanup returned from the
    // lifted branch, so the resize/transitionend listeners can never leak.
    if (!liftForTray) {
      apply(0);
      return;
    }

    let cancelled = false;
    let resizeTimer = 0;

    // `damp` applies TRAY_OPEN_PILL_INITIAL_TRAVEL_FRACTION — used for the
    // first (pre-anchor-resolve) measurement only, so the pill rises part way
    // and eases down instead of overshooting and snapping back. Re-measures
    // after a resize take the full value: the pill is already parked, so
    // there is no rise to soften.
    const measureAndApply = ({ damp = false }: { damp?: boolean } = {}) => {
      if (cancelled) return;
      const pillEl = elementRef.current;
      const cardsEl = povCardsRefs.get(seatId)?.current;
      if (!pillEl || !cardsEl) {
        apply(TRAY_OPEN_BET_PILL_LIFT_FALLBACK_PX);
        return;
      }
      const scale = visualScaleOf(pillEl);
      const scope = pillEl.closest('[data-table-view-root]') ?? document;
      const trayEl = scope.querySelector('[data-bet-tray]');

      // Undo our own current lift (it rides inside the scaled space).
      const pillStaticBottom =
        pillEl.getBoundingClientRect().bottom + trayLiftPxRef.current * scale;
      const cardsTransform = getComputedStyle(cardsEl).transform;
      // The matrix's ty is in the cards' OWN (scaled) coordinate space, while
      // the rect is in viewport space — so it has to be scaled before it can
      // cancel out, or the pill lands ~(1 - scale) × lift too low.
      const cardsTy =
        cardsTransform && cardsTransform !== 'none' ? new DOMMatrixReadOnly(cardsTransform).m42 : 0;
      const cardsRect = cardsEl.getBoundingClientRect();
      const cardsStaticTop = cardsRect.top - cardsTy * scale;
      const cardsStaticBottom = cardsRect.bottom - cardsTy * scale;
      // Where the cards will end up: the SAME rule the group itself applies
      // (hooks/useTrayClearanceLift) — clear the tray's real top edge by the
      // shared gap — evaluated from the same rects, so the two agree without
      // any handoff and without either predicting a viewport formula.
      const cardsLift = trayEl
        ? Math.max(
            0,
            cardsStaticBottom - trayEl.getBoundingClientRect().top + TRAY_CLEARANCE_GAP_PX
          )
        : 0;
      const liftedCardsTop = cardsStaticTop - cardsLift;
      const neededVisualPx = pillStaticBottom - liftedCardsTop + TRAY_OPEN_PILL_CARD_GAP_PX;
      const needed = neededVisualPx / scale;
      apply(
        Math.max(0, Math.round(damp ? needed * TRAY_OPEN_PILL_INITIAL_TRAVEL_FRACTION : needed))
      );
    };

    measureAndApply({ damp: true });

    // Viewport changes (rotation out/back, split view) re-resolve the anchors
    // without remounting this component — re-measure instead of drifting.
    const onResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => measureAndApply(), 150);
    };
    window.addEventListener('resize', onResize);

    // Settle verifier: after the glide completes, confirm the REAL gap and
    // apply one exact correction if the prediction missed (>2px).
    const settle = (e: TransitionEvent) => {
      if (e.propertyName !== 'transform' || cancelled) return;
      const pillEl = elementRef.current;
      const cardsEl = povCardsRefs.get(seatId)?.current;
      if (!pillEl || !cardsEl) return;
      const scale = parseFloat(getComputedStyle(pillEl).getPropertyValue('--seat-scale')) || 1;
      const gapNow = cardsEl.getBoundingClientRect().top - pillEl.getBoundingClientRect().bottom;
      const excess = gapNow - TRAY_OPEN_PILL_CARD_GAP_PX;
      if (Math.abs(excess) > 2) {
        apply(Math.max(0, Math.round(trayLiftPxRef.current - excess / scale)));
      }
    };
    const pillElForListener = elementRef.current;
    pillElForListener?.addEventListener('transitionend', settle);

    return () => {
      cancelled = true;
      window.clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
      pillElForListener?.removeEventListener('transitionend', settle);
    };
  }, [liftForTray, seatId, povCardsRefs]);

  const playerAction = useTableSelector(
    (table) => ((table.game as any)[`p${seatId}action`] as string) || ''
  );

  // Trigger flying-chip animations from the committed bet to the pot. Fires only when
  // a positive player bet transitions to 0 — i.e. the round closes and chips
  // are actually transferred to the pot. We re-mount BetChips by changing its
  // key so each collection re-fires.
  //
  // Initialized to `null` (not 0) so the very first render — which may arrive
  // mid-hand with a non-zero bet, or after a resize-driven remount — does not
  // mistake the initial value for a transition and fire spuriously.
  const [chipFlightId, setChipFlightId] = useState(0);
  const [allInFlight, setAllInFlight] = useState(false);
  const prevPlayerBetRef = useRef<number | null>(null);

  // Trigger the over-bet (uncalled portion) return animation. Fires on the
  // transition from "no over-bet" → "has over-bet" so it only plays once per
  // calculated over-bet, even as `hand` phase cycles through values while the
  // over-bet stays set on the server payload.
  const [returnedChipFlightId, setReturnedChipFlightId] = useState(0);
  const wasOverBetActiveRef = useRef(false);
  const hasOverBetForSeat = tableOverBet?.seatId === seatId && (tableOverBet?.bet ?? 0) > 0;

  useEffect(() => {
    // Arriving at a table that already has an over-bet returned: record it as
    // seen without flying the chips back again.
    if (hasOverBetForSeat && !wasOverBetActiveRef.current && !shouldSnap()) {
      setReturnedChipFlightId((id) => id + 1);
    }
    wasOverBetActiveRef.current = hasOverBetForSeat;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOverBetForSeat]);

  useEffect(() => {
    const betValue = +seat.betDisplay;
    const prevBetValue = prevBetValueRef.current;
    prevBetValueRef.current = betValue;

    if (!seat.ID) {
      pendingOwnCueRef.current = null;
      setIsShow(false);
      setPlayerBet(0);
      return;
    }

    if (betValue > 0) {
      // YOUR OWN WAGER is ARMED here, on the wire edge, and PLAYED from the
      // isShow effect below — so the sound lands together with the pill.
      // Round 2 of dev review #3747 found the own blind double-playing and
      // moved the cue itself onto this edge; round 3 found the real cause:
      // the reveal stagger below was PANEL-scoped (`!shouldSnap()` folds in
      // "nobody is looking at this panel"), so the table's two mounted copies
      // staged isShow DEALER_DELAY apart and their isShow-gated claims missed
      // claimCue's dedup window. The stagger is table-scoped now (see
      // `arrivalSnap` below), every copy stages isShow identically, and the
      // cue is safely isShow-gated again — both claims land in one window AND
      // the sound no longer leads the pill on the watched copy.
      //
      // `prevBetValue !== null` is the guard this ref was built for: a bet
      // already on the felt at mount/remount/arrival must simply BE there —
      // no cue — which also keeps a copy mounted mid-hand (grid toggled on)
      // silent while the resident copy already cued: no edge, nothing armed,
      // so its reveal plays nothing (structural now, not borrowed from
      // useSound's arrival window). `prevBetValue !== betValue` makes a
      // same-value re-broadcast a no-op while a raise/top-up still arms.
      if (isSelfSeat && prevBetValue !== null && prevBetValue !== betValue) {
        pendingOwnCueRef.current = betValue;
      }
      // The blinds/ante reveal is staged behind the dealer-button move during
      // live play; on arrival the blinds are already posted, so show them at
      // once rather than replaying that stagger. ARRIVAL-scoped, not panel-
      // scoped (`arrivalSnap`, dev review #3747 round 3): the predicate
      // depends only on the gameID, so the carousel panel and the grid cell
      // stage isShow at the same moment. The accepted cost is that a
      // background panel's pill staggers too — which nobody is looking at.
      if ((hand === '2' || hand === '3' || hand === '4') && !arrivalSnap()) {
        setTimeout(() => {
          setIsShow(true);
        }, DEALER_DELAY);
      } else {
        setIsShow(true);
      }
      setPlayerBet(betValue);
      return;
    }

    // betValue === 0 — chips returning to the pot. Only play sound and run
    // the hide-delay animation when this is a real positive → 0 transition.
    // `prevBetValue === null` means first render (mount/remount/resize), so
    // skip side effects to avoid the animation re-firing on every resize.
    //
    // A wager collected before its staged reveal ever fired must not leave a
    // stale armed cue behind — the pill it belonged to is gone.
    pendingOwnCueRef.current = null;
    setPlayerBet(0);
    // `!shouldSnap()`: during an arrival/resync burst the collection already
    // happened elsewhere — drop the pill and its cue instantly instead of
    // playing the chips-to-pot sound and holding the pill for the flight.
    if (prevBetValue !== null && prevBetValue > 0 && !shouldSnap()) {
      if (shouldPlayBetSounds) playChipsToPot();
      setTimeout(() => {
        setIsShow(false);
      }, CHIPS_DELAY + 300);
    } else {
      setIsShow(false);
    }
  }, [hand, tableOverBet, seat, seat.ID, seat.lbet, seatId, shouldPlayBetSounds, playChipsToPot]);

  useEffect(() => {
    const snap = shouldSnap();
    // BOTH chip cues fire here, on the pill's reveal, so sound and visual land
    // together. Safe for the own cue again since dev review #3747 round 3: the
    // reveal stagger is table-scoped (`arrivalSnap`), so every mounted copy's
    // isShow flips at the same moment and their claims share one dedup window.
    if (isShow && shouldPlayBetSounds) {
      if (isSelfSeat) {
        // YOUR OWN wager — but only when the wire-edge effect above ARMED it
        // for this amount: a copy that mounted with the bet already on the
        // felt (arrival state, not news) armed nothing and stays silent.
        if (pendingOwnCueRef.current === playerBet) {
          pendingOwnCueRef.current = null;
          // Blinds, limps and min bets are one chip; anything above the big
          // blind is the handful. Read through a ref, and deliberately NOT a
          // dependency: a tournament blind level rising mid-hand must not
          // re-fire this cue.
          const cue = resolveChipCue({ amount: playerBet, bigBlind: bigBlindRef.current });
          // crossTable: a turn-outcome cue must reach you from a table you
          // are not looking at (EN-10585 AC1). The amount is in the key so
          // consecutive wagers by this seat are distinct cues, while the
          // table's other mounted copy firing the same wager is not.
          (cue === 'smallBet' ? playSmallBetOwn : playBetChipsOwn)({
            dedupeKey: `chips:${seatId}:${playerBet}`
          });
        }
      } else if (!snap) {
        // OPPONENT wagers: ambient table texture — table-local, attenuated
        // (EN-10585 AC2), and suppressed while nobody is watching this panel
        // (`snap`). Blinds, limps and min bets are one chip; anything above
        // the big blind is the handful — same ref, same non-dependency rule.
        const cue = resolveChipCue({ amount: playerBet, bigBlind: bigBlindRef.current });
        // The same sample choice as the own path so the action→sound pairing
        // stays learnable — just quieter and local.
        (cue === 'smallBet' ? playSmallBet : playBetChips)();
      }
    }
    setNewPlayerBet(playerBet);

    if (textRef.current && isShow) {
      // Pop-in: scale from 0 with bounce — or, on arrival, the pill is simply
      // already there at full size.
      popTweenRef.current = snapOrFromTo(
        snap,
        textRef.current,
        { scale: 0, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.35, ease: 'back.out(2)' }
      );
    }

    return () => {
      popTweenRef.current?.kill();
    };
  }, [playChipsToPot, playerBet, isShow]);

  // Fire flying chips only on the transition from a positive bet down to 0
  // (chips transferred to the pot at the end of a betting round). The all-in
  // variant is captured at trigger time so the visual matches the action even
  // if `playerAction` updates a tick later.
  useEffect(() => {
    const prev = prevPlayerBetRef.current;
    prevPlayerBetRef.current = playerBet;

    // Skip the very first render — `prev === null` means we have no baseline
    // to compare against, which is what a mount or resize-driven remount looks
    // like. The animation should only fire on a real bet→0 transition.
    if (prev === null) return;

    // A collection that lands inside the snap window belongs to the state the
    // player arrived at — don't mount a flight for it at all.
    if (prev > 0 && playerBet === 0 && !shouldSnap()) {
      setAllInFlight(playerAction.toLowerCase() === 'allin');
      setChipFlightId((id) => id + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerBet, playerAction]);

  return (
    <>
      {position && chipFlightId > 0 && (
        <BetChips
          key={chipFlightId}
          from={position}
          originElement={elementRef.current}
          allIn={allInFlight}
        />
      )}
      {seatPosition && returnedChipFlightId > 0 && (
        <>
          <ReturnedBetChips key={`return-chips-${returnedChipFlightId}`} to={seatPosition} />
          <ReturnedBetMessage
            key={`return-msg-${returnedChipFlightId}`}
            at={seatPosition}
            isPortrait={isPortrait}
          />
        </>
      )}
      {isShow && (
        <div
          ref={elementRef}
          style={{
            ...BaseStyle,
            left: position ? `${position.leftPct}%` : 0,
            // The POV's pill is hung off its own seat, clear of that seat's
            // dealer marker and hole cards — see selfBetTop.
            top: position
              ? selfBetTop(position.topPct, seatPosition?.topPct, isSelfSeat, isPortrait)
              : 0,
            // Bets render outside the (scaled) seat wrappers, so apply the same
            // responsive table scale here — otherwise they stay full-size while
            // seats shrink on small / multi-table layouts.
            // The EN-10420 lift rides inside the scaled space (after scale) so
            // it shrinks with the pill in scaled cells.
            transform: `translate(-50%, -50%) scale(var(--seat-scale, 1))${
              liftForTray && trayLiftPx ? ` translateY(-${trayLiftPx}px)` : ''
            }`,
            transition: `transform ${TRAY_OPEN_CARD_LIFT_MS}ms ease-out`,
            transformOrigin: 'center center'
          }}>
          <div ref={textRef} style={betDisplayStyle}>
            <PokerChip size={18} color='red' />
            <span style={betAmountStyle}>
              {tableMoneyFormatterCompact(newPlayerBet, tableType)}
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default Bet;

const BaseStyle: CSSProperties = {
  position: 'absolute',
  display: 'flex',
  // Table z-order model (Jason 8/13, EN-10420): bet chips are objects ON the
  // felt, so they share the dealer button's plane (z5) and sit UNDER the POV
  // seat (z6 — cards in the player's hand). When a pill overlaps the POV hole
  // cards, the cards win; the overlap itself is fine and positional caps
  // above stay as they are.
  zIndex: 5
};

const betDisplayStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.3rem',
  padding: '0.2rem 0.5rem 0.2rem 0.25rem',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  borderRadius: '20px',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  whiteSpace: 'nowrap'
};

const betAmountStyle: CSSProperties = {
  // The bet pill is scaled by --seat-scale (see the wrapper transform). Counter-
  // scale the amount so it stays legible without ballooning the pill on small
  // screens (a high floor makes the pill much wider than proportional and bleeds
  // onto the avatars). ~9px floor (beta feedback: bumped) balances readability
  // vs. overlap. Also scales with the player's Table View Size.
  // No view-scale multiplier: `--seat-scale` carries it now, and the pill's
  // wrapper transform already applies that scale.
  fontSize: 'max(0.75rem, calc(9px / var(--seat-scale, 1)))',
  fontWeight: 600,
  color: '#e8e8e8'
};
